import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DocumentTextIcon, ClipboardDocumentCheckIcon, TrashIcon, ArchiveBoxIcon, DocumentDuplicateIcon, EyeIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../../lib/supabase-auth';
import { useAuth } from '../../../contexts/AuthContextLazy';
import ThreeDotsMenu from '../../../components/portal/ThreeDotsMenu';
import ConfirmDialog from '../../../components/portal/ConfirmDialog';
import { showSuccess, showError, showInfo } from '../../../utils/toast';
import '../../../styles/portal-ui-polish.css';

interface ComplianceDocument {
  id: string;
  document_type: 'needs_analysis' | 'reason_why';
  document_number: string;
  status: 'draft' | 'pending_signature' | 'signed' | 'archived';
  client_id: string;
  created_at: string;
  updated_at: string;
  signed_at: string | null;
  client?: {
    id: string;
    name: string;
    primary_email: string;
  };
}

interface ComplianceStats {
  inProgress: number;
  awaitingSignature: number;
  completedThisMonth: number;
  completedYTD: number;
}

export default function ComplianceWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [documents, setDocuments] = useState<ComplianceDocument[]>([]);
  const [stats, setStats] = useState<ComplianceStats>({
    inProgress: 0,
    awaitingSignature: 0,
    completedThisMonth: 0,
    completedYTD: 0
  });
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState<{ isOpen: boolean; documentId: string | null }>({
    isOpen: false,
    documentId: null
  });

  useEffect(() => {
    fetchDocuments();
    fetchStats();
  }, [user]);

  const fetchDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('compliance_documents')
        .select(`
          *,
          client:parties(id, name, primary_email)
        `)
        .eq('advisor_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error fetching documents:', error);
        }

      }
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Failed to load documents');
        }

      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfYear = new Date(now.getFullYear(), 0, 1);

      // Fetch all stats in parallel
      const [inProgressResult, awaitingResult, monthResult, yearResult] = await Promise.all([
        supabase
          .from('compliance_documents')
          .select('id', { count: 'exact' })
          .eq('advisor_id', user?.id)
          .eq('status', 'draft'),
        supabase
          .from('compliance_documents')
          .select('id', { count: 'exact' })
          .eq('advisor_id', user?.id)
          .eq('status', 'pending_signature'),
        supabase
          .from('compliance_documents')
          .select('id', { count: 'exact' })
          .eq('advisor_id', user?.id)
          .eq('status', 'signed')
          .gte('signed_at', startOfMonth.toISOString()),
        supabase
          .from('compliance_documents')
          .select('id', { count: 'exact' })
          .eq('advisor_id', user?.id)
          .eq('status', 'signed')
          .gte('signed_at', startOfYear.toISOString())
      ]);

      setStats({
        inProgress: inProgressResult.count || 0,
        awaitingSignature: awaitingResult.count || 0,
        completedThisMonth: monthResult.count || 0,
        completedYTD: yearResult.count || 0
      });
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error fetching stats:', error);
        }

      }
    }
  };

  const handleNewDocument = () => {
    navigate('/portal/compliance/new');
  };

  const handleContinueDocument = (docId: string) => {
    navigate(`/portal/compliance/edit/${docId}`);
  };

  const handleViewDocument = (docId: string) => {
    navigate(`/portal/compliance/view/${docId}`);
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: { 
        emoji: '🔵', 
        text: 'Draft', 
        class: 'text-blue-600 bg-blue-50 border-blue-200',
        dotClass: 'bg-blue-500'
      },
      pending_signature: { 
        emoji: '🟡', 
        text: 'Pending Signature', 
        class: 'text-yellow-600 bg-yellow-50 border-yellow-200',
        dotClass: 'bg-yellow-500'
      },
      signed: { 
        emoji: '🟢', 
        text: 'Signed', 
        class: 'text-green-600 bg-green-50 border-green-200',
        dotClass: 'bg-green-500'
      },
      archived: { 
        emoji: '⚫', 
        text: 'Archived', 
        class: 'text-gray-600 bg-gray-50 border-gray-200',
        dotClass: 'bg-gray-500'
      }
    };
    const badge = badges[status as keyof typeof badges] || badges.draft;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${badge.class}`}>
        <span className={`w-2 h-2 rounded-full ${badge.dotClass} mr-1.5`}></span>
        <span>{badge.text}</span>
      </span>
    );
  };

  const getDocumentTypeDisplay = (type: string) => {
    return type === 'needs_analysis' ? 'Needs Analysis' : 'Reason Why';
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      const { error } = await supabase
        .from('compliance_documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;
      
      showSuccess('Document deleted successfully');
      fetchDocuments();
      fetchStats();
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error deleting document:', error);
        }

      }
      showError('Failed to delete document');
    }
  };

  const handleArchiveDocument = async (documentId: string) => {
    try {
      const { error } = await supabase
        .from('compliance_documents')
        .update({ status: 'archived' })
        .eq('id', documentId);

      if (error) throw error;
      
      showSuccess('Document archived successfully');
      fetchDocuments();
      fetchStats();
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error archiving document:', error);
        }

      }
      showError('Failed to archive document');
    }
  };

  const handleDuplicateDocument = async (document: ComplianceDocument) => {
    try {
      const newDocNumber = `${document.document_number}-COPY-${Date.now()}`;
      
      const { data, error } = await supabase
        .from('compliance_documents')
        .insert({
          document_type: document.document_type,
          document_number: newDocNumber,
          status: 'draft',
          client_id: document.client_id,
          advisor_id: user?.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      
      showSuccess('Document duplicated successfully');
      navigate(`/portal/compliance/edit/${data.id}`);
    } catch (error) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error('Error duplicating document:', error);
        }

      }
      showError('Failed to duplicate document');
    }
  };

  const handleCreateProject = async (document: ComplianceDocument) => {
    try {
      // Create a visual journey project from the compliance document
      const projectData = {
        name: `${document.client?.name} - ${getDocumentTypeDisplay(document.document_type)}`,
        client_id: document.client_id,
        type: 'compliance',
        metadata: {
          compliance_document_id: document.id,
          document_type: document.document_type,
          created_from: 'compliance_wizard'
        }
      };

      showInfo('Visual Journey integration coming soon');
      
      // TODO: Integrate with visual journey system
      // const { data, error } = await supabase
      //   .from('visual_journey_projects')
      //   .insert(projectData)
      //   .select()
      //   .single();
      
      // if (error) throw error;
      // navigate(`/portal/visual-journey/${data.id}`);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error creating project:', error);
      }
      showError('Failed to create project');
    }
  };

  const handleViewJourney = (document: ComplianceDocument) => {
    // Show visual journey preview for the document
    showInfo('Visual journey preview coming soon');
    
    // TODO: Open visual journey modal or navigate to journey view
    // navigate(`/portal/visual-journey/preview/${document.id}`);
  };

  return (
    <div className="portal-page-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Actions Bar */}
        <div className="flex items-center justify-end mb-8">
          <button
            onClick={handleNewDocument}
            className="portal-button-primary flex items-center"
          >
            <DocumentTextIcon className="h-5 w-5 mr-2" />
            New Document
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Quick Start Card */}
          <div className="portal-card-elevated p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">📝 Quick Start</h3>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/portal/compliance/new?type=needs_analysis')}
                className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <div className="flex items-center">
                  <ClipboardDocumentCheckIcon className="h-5 w-5 text-amber-500 mr-2" />
                  <span className="font-medium">Needs Analysis</span>
                </div>
              </button>
              <button
                onClick={() => navigate('/portal/compliance/new?type=reason_why')}
                className="w-full text-left p-3 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <div className="flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-amber-500 mr-2" />
                  <span className="font-medium">Reason Why Letter</span>
                </div>
              </button>
            </div>
            <button
              onClick={handleNewDocument}
              className="mt-4 w-full portal-button-primary"
            >
              Start Wizard
            </button>
          </div>

          {/* In Progress Card */}
          <div className="portal-stats-card relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-amber-100 rounded-full -mr-12 -mt-12 opacity-50"></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 relative">📊 In Progress</h3>
            <div className="space-y-3 relative">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Drafts</span>
                <span className="font-semibold text-gray-900">{stats.inProgress}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Awaiting Signature</span>
                <span className="font-semibold text-amber-600">{stats.awaitingSignature}</span>
              </div>
              
              {stats.inProgress + stats.awaitingSignature > 0 && (
                <div className="pt-3 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Total Active</span>
                    <span className="font-medium text-gray-700">
                      {stats.inProgress + stats.awaitingSignature}
                    </span>
                  </div>
                  <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-amber-500 rounded-full transition-all duration-500"
                      style={{ 
                        width: `${((stats.inProgress) / (stats.inProgress + stats.awaitingSignature)) * 100}%` 
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Drafts</span>
                    <span>Pending</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Completed Card */}
          <div className="portal-stats-card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">✅ Completed</h3>
            <div className="space-y-2 text-gray-600">
              <p>• {stats.completedThisMonth} this month</p>
              <p>• {stats.completedYTD} YTD</p>
            </div>
            <button
              onClick={() => navigate('/portal/compliance/documents')}
              className="mt-4 text-amber-600 hover:text-amber-700 font-medium"
            >
              View All →
            </button>
          </div>
        </div>

        {/* Recent Documents Table */}
        <div className="portal-table-container">
          <div className="portal-table-header">
            <h3 className="text-lg font-semibold text-gray-900">Recent Documents</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                      Loading documents...
                    </td>
                  </tr>
                ) : documents.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                      No documents yet. Click "New Document" to get started.
                    </td>
                  </tr>
                ) : (
                  documents.map((doc) => (
                    <tr key={doc.id} className="portal-table-row">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {doc.client?.name || 'Unknown Client'}
                        </div>
                        <div className="text-sm text-gray-500">{doc.document_number}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getDocumentTypeDisplay(doc.document_type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {getStatusBadge(doc.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center space-x-3">
                          {doc.status === 'draft' ? (
                            <button
                              onClick={() => handleContinueDocument(doc.id)}
                              className="text-amber-600 hover:text-amber-700 font-medium"
                            >
                              Continue
                            </button>
                          ) : (
                            <button
                              onClick={() => handleViewDocument(doc.id)}
                              className="text-amber-600 hover:text-amber-700 font-medium"
                            >
                              View
                            </button>
                          )}
                          <ThreeDotsMenu
                            items={[
                              {
                                label: 'View Journey',
                                onClick: () => handleViewJourney(doc),
                                icon: <EyeIcon className="h-4 w-4" />
                              },
                              {
                                label: 'Create Project',
                                onClick: () => handleCreateProject(doc),
                                icon: <RocketLaunchIcon className="h-4 w-4" />,
                                className: doc.status === 'signed' ? 'text-gray-700 hover:text-gray-900' : 'text-gray-400 cursor-not-allowed'
                              },
                              {
                                divider: true
                              },
                              {
                                label: 'Duplicate',
                                onClick: () => handleDuplicateDocument(doc),
                                icon: <DocumentDuplicateIcon className="h-4 w-4" />
                              },
                              {
                                label: 'Archive',
                                onClick: () => handleArchiveDocument(doc.id),
                                icon: <ArchiveBoxIcon className="h-4 w-4" />,
                                className: doc.status === 'archived' ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:text-gray-900'
                              },
                              {
                                divider: true
                              },
                              {
                                label: 'Delete',
                                onClick: () => setDeleteDialog({ isOpen: true, documentId: doc.id }),
                                icon: <TrashIcon className="h-4 w-4" />,
                                className: 'text-red-600 hover:text-red-700'
                              }
                            ].filter(item => item.label !== 'Archive' || doc.status !== 'archived')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={deleteDialog.isOpen}
          onClose={() => setDeleteDialog({ isOpen: false, documentId: null })}
          onConfirm={() => {
            if (deleteDialog.documentId) {
              handleDeleteDocument(deleteDialog.documentId);
            }
          }}
          title="Delete Document"
          message="Are you sure you want to delete this document? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          isDestructive={true}
        />
      </div>
    </div>
  );
}